// src/pages/admin/AdminLogin.js
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { jwtDecode } from "jwt-decode";
import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";

// ✅ Validation schema
const adminLoginSchema = yup.object().shape({
  username: yup.string().required("Username is required"),
  password: yup.string().required("Password is required"),
  user_type: yup.string().required("User type is required"),
});

export default function AdminLogin() {
  const [apiError, setApiError] = useState(null);
  const navigate = useNavigate();
  const { loginAdmin } = useAuth(); // Reuse context, but we’ll store separately for admins

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(adminLoginSchema),
  });

  const onSubmit = async (credentials) => {
    try {
      setApiError(null);

      // Map user_type to API values
      const apiCredentials = {
        ...credentials,
        user_type: credentials.user_type === "Admin" ? "A" : "M"
      };

      const res = await api.post("/admin/auth/login", apiCredentials);

      if (res.data.status === "success") {
        const { token } = res.data;

        // ✅ Store admin token separately
        if (token) {
          loginAdmin(token);

          // After login, check user role to determine navigation path
          const decoded = jwtDecode(token);
          const isMaster = decoded.role === "M";

          const targetPath = isMaster ? "/dashboard" : "/admin/dashboard";
        //  console.log(`AdminLogin: Login successful, navigating to ${targetPath} (user role: ${decoded.role})`);
          navigate(targetPath);
        }
      } else {
        setApiError(res.data.message || "Invalid admin credentials");
      }
    } catch (error) {
      console.error("Admin login error:", error);
      setApiError(error.response?.data?.message || "Server error. Please try again later.");
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100">
      <div className="bg-white shadow-lg rounded-lg w-full max-w-md p-8">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/images/logobanner.jpg"
            alt="Admin Logo"
            className="h-12"
          />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center text-blue-800 mb-2">
          Admin Portal
        </h2>

        {/* API error */}
        {apiError && <p className="text-red-600 text-center mb-4">{apiError}</p>}

        <p className="text-center text-gray-600 mb-6">
          Please sign in with your admin credentials
        </p>

        {/* Form */}
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {/* Username */}
          <div>
            <input
              type="text"
              placeholder="Admin Username"
              {...register("username")}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.username ? "border-red-500" : ""
                }`}
            />
            {errors.username && (
              <p className="text-red-500 text-sm mt-1">{errors.username.message}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <input
              type="password"
              placeholder="Admin Password"
              {...register("password")}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.password ? "border-red-500" : ""
                }`}
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
            )}
          </div>

          {/* User Type */}
          <div>
            <select
              {...register("user_type")}
              defaultValue="Admin"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.user_type ? "border-red-500" : ""
                }`}
            >
              <option value="Admin">Admin</option>
              <option value="Master">Master</option>
            </select>
            {errors.user_type && (
              <p className="text-red-500 text-sm mt-1">{errors.user_type.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50"
          >
            {isSubmitting ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
